import { Context, createContext, FunctionComponent, useContext } from 'react';
import { IReactionDisposer, makeAutoObservable, reaction } from 'mobx';

import { debounce, isArray, isEnumMember, queryAPI } from '@cutwise/utils';

import { S3UploadAPI } from '@/dmdrive/core/api/S3UploadAPI';
import { AppError } from '@/dmdrive/core/entities/AppError';
import { ConfirmService } from '@/dmdrive/core/service/ConfirmService';
import { store } from '@/dmdrive/store';
import { TopicAPI } from '@/dmdrive/topic/api/TopicAPI';
import { TopicViewerStore } from '@/dmdrive/topic/components/Topic/TopicViewerStore';
import { ToolbarQuery } from '@/dmdrive/topic/constants/query';
import { DEFAULT_SNAPSHOT_ID } from '@/dmdrive/topic/constants/snapshot';
import { ConferenceState } from '@/dmdrive/topic/constants/topic';
import { Snapshot, SnapshotMeta } from '@/dmdrive/topic/entities/Snapshot';
import { Topic, TopicModel } from '@/dmdrive/topic/entities/Topic';
import { NotifyService } from '@/dmdrive/topic/services/NotifyService';

import { InteractionPoint } from '@carbon/dm-industrial/lib/dm-industrial';

import { SidebarTab } from './TopicStore.constants';
import { TopicWSStore } from './TopicWSStore';

export class TopicStore {
  isFetching = true;
  topic: Nullable<Topic> = null;
  error: Nullable<AppError> = null;

  isLeftSidebarOpen = false;
  isRightSidebarOpen = false;

  isCreatingSnapshot = false;

  activeTab = SidebarTab.Remarks;

  conferenceState = ConferenceState.None;

  readonly topicWSStore: TopicWSStore;
  readonly topicViewerStore: TopicViewerStore;

  readonly topicAPI: TopicAPI;
  readonly s3UploadAPI: S3UploadAPI;

  private reactions: IReactionDisposer[] = [];

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });

    this.topicAPI = new TopicAPI();
    this.s3UploadAPI = new S3UploadAPI();
    this.topicWSStore = new TopicWSStore(this);
    this.topicViewerStore = new TopicViewerStore(this);
  }

  get me() {
    return this.topic?.topicUsers.find((member) => member.id === store.appStore.user?.id) ?? null;
  }

  get isJoinedConference() {
    return this.conferenceState === ConferenceState.Follow || this.conferenceState === ConferenceState.Explore;
  }

  get isControlDisabled() {
    return this.conferenceState === ConferenceState.Follow && !this.me?.isMaster;
  }

  get masterRequestCount() {
    return (
      this.topic?.topicUsers?.reduce((result, user) => (!user.isMe && user.requestMaster ? result + 1 : result), 0) ?? 0
    );
  }

  get settings() {
    return {
      ...this.topicViewerStore.settings,
      activeTab: this.activeTab,
    };
  }

  get topicSnapshots() {
    if (!isArray(this.topic?.snapshots)) {
      return [];
    }

    return this.topic!.snapshots.filter(({ type }) => type !== DEFAULT_SNAPSHOT_ID).sort(
      (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime(),
    );
  }

  *initTopicStore(id: string) {
    this.isFetching = true;

    try {
      yield this.getTopic(id);

      // first, apply params from string
      this.applyQuery();

      // second, apply settings from default snapshot
      this.setPresetFromSnapshot(queryAPI.getKey(ToolbarQuery.Snapshot));

      this.setActiveTab(this.topic?.areRemarksAvailable ? SidebarTab.Remarks : SidebarTab.Participants);

      // subscribe in the end to avoid side effect when apply settings
      this.subscribeToReactions();
    } catch (e) {
      this.error = new AppError(e);
    }

    this.isFetching = false;
  }

  *getTopic(id: string) {
    const topic: TopicModel = yield this.topicAPI.getTopicById(id);

    if (this.topic) {
      this.topic.merge(topic);
    } else {
      this.topic = new Topic(topic);
    }

    this.topicViewerStore.viewer?.putRemarks?.(this.topic!.marksMetas);
  }

  setActiveTab(tab: SidebarTab) {
    this.activeTab = tab;
  }

  *joinConference() {
    if (!this.topic) {
      return;
    }

    this.conferenceState = ConferenceState.Creating;

    try {
      yield this.topicWSStore.openTopic();

      yield this.getTopic(this.topic.id);

      this.setConferenceState(ConferenceState.Follow);
    } catch (e) {
      NotifyService.error(e);

      this.conferenceState = ConferenceState.None;
    }
  }

  *leaveConference() {
    this.resetConferenceState();

    if (this.topic?.id) {
      yield this.getTopic(this.topic.id);
    }
  }

  setConferenceState(state: ConferenceState) {
    if (state === ConferenceState.Follow) {
      if (!this.me?.isMaster) {
        this.topicWSStore?.requestForState();
      } else {
        this.notifyUserIsMaster();
      }
    }

    this.conferenceState = state;
  }

  setListenMode(listenMode: boolean) {
    if (!this.isJoinedConference) {
      return;
    }

    this.topicViewerStore.viewer?.apply({ listenMode });
  }

  *addTextMark(point: InteractionPoint, text: string) {
    try {
      const meta = this.topicViewerStore.viewer?.putText?.(point, text);

      if (meta) {
        this.topic?.merge(yield this.topicAPI.addTopicMarks(this.topic!.id, [{ type: 'carbon', meta }]));

        this.topicWSStore.publishSystem('topicUpdate');

        this.topicViewerStore.viewer?.putRemarks?.(this.topic!.marksMetas);
      }
    } catch (e) {
      NotifyService.error(e);
    }
  }

  *deleteMark(id: string) {
    try {
      this.topic?.merge(yield this.topicAPI.deleteTopicMarks(this.topic!.id, [id]));

      this.topicWSStore.publishSystem('topicUpdate');

      this.topicViewerStore.viewer?.putRemarks?.(this.topic!.marksMetas);
    } catch (e) {
      NotifyService.error(e);
    }
  }

  *deleteMarkByMetaId(id: string) {
    try {
      let metaId = '';

      if (this.topic?.marks) {
        for (let i = 0; i < this.topic?.marks.length; i++) {
          if (this.topic?.marks[i].meta.id === id) {
            metaId = this.topic?.marks[i].id;
          }
        }
      } else {
        return;
      }

      this.topic?.merge(yield this.topicAPI.deleteTopicMarks(this.topic!.id, [metaId]));

      this.topicWSStore.publishSystem('topicUpdate');

      this.topicViewerStore.viewer?.putRemarks?.(this.topic!.marksMetas);
    } catch (e) {
      NotifyService.error(e);
    }
  }

  *deleteAllMarks() {
    try {
      const shouldRemove: boolean = yield ConfirmService.deleteAllRemarks();

      if (shouldRemove) {
        this.topic?.merge(
          yield this.topicAPI.deleteTopicMarks(this.topic!.id, this.topic?.marks?.map(({ id }) => id) ?? []),
        );

        this.topicWSStore.publishSystem('topicUpdate');

        this.topicViewerStore.viewer?.putRemarks?.(this.topic!.marksMetas);
      }
    } catch (e) {
      NotifyService.error(e);
    }
  }

  *createSnapshot() {
    try {
      this.isCreatingSnapshot = true;

      const file: Nullable<File> = yield this.topicViewerStore.viewer?.getScreenshot?.();

      let preview: Nullable<string> = null;

      if (file) {
        const { url } = yield this.s3UploadAPI.uploadImage(file);

        preview = url;
      }

      yield this.topicAPI.createTopicSnapshot(this.topic!.id, {
        type: 'private',
        data: {
          title: `Snapshot ${this.topicSnapshots.length + 1}`,
          state: this.settings,
          preview,
          edited: false,
        },
      });

      yield this.getTopic(this.topic!.id);
    } catch (e) {
      NotifyService.error(e);
    } finally {
      this.isCreatingSnapshot = false;
    }
  }

  *deleteSnapshot(snapshot: Snapshot) {
    try {
      const shouldRemove: boolean = yield ConfirmService.deleteSnapshot(snapshot.title);

      if (shouldRemove) {
        // remove from list as soon as possible
        this.topic!.snapshots = this.topic!.snapshots.filter(({ id }) => snapshot.id !== id);

        yield this.topicAPI.deleteTopicSnapshot(this.topic!.id, snapshot.id);

        yield this.getTopic(this.topic!.id);
      }
    } catch (e) {
      NotifyService.error(e);

      throw e;
    }
  }

  *updateSnapshot(snapshotId: string, data: SnapshotMeta, type: 'private' | 'default' | 'public') {
    try {
      yield this.topicAPI.updateTopicSnapshot(this.topic!.id, snapshotId, {
        data: {
          ...data,
          edited: true,
        },
        type: type,
      });

      yield this.getTopic(this.topic!.id);
    } catch (e) {
      NotifyService.error(e);

      throw e;
    }
  }

  updateTopic = debounce(async () => {
    if (!this.topic) {
      return;
    }

    try {
      await this.topicAPI.updateTopic(this.topic.id, this.topic.meta);

      this.topicWSStore.publishSystem('topicUpdate');
    } catch (e) {
      NotifyService.error(e);
    }
  }, 0);

  setPresetFromSnapshot(snapshotId: string) {
    // here we can pass id or type, for example type might be "default"
    let snapshot = this.topic!.snapshots.find(({ id }) => id === snapshotId);

    if (!snapshot) {
      // in case if pass id of snapshot that does not exist
      snapshot = this.topic!.snapshots.find(({ type }) => type === DEFAULT_SNAPSHOT_ID);
    }

    const state = snapshot?.state;

    if (state) {
      Object.keys(state).forEach((key) => {

        if (key === 'fitness' && (state[key] as number) > -2) {
          this.topic?.setMetaValue('fitness', (state[key] as number) );
        }

        if (typeof state[key] !== 'undefined') {
          if (key in this) {
            // TODO add proper typing
            // @ts-ignore
            this[key] = state[key];
          } else if (key in this.topicViewerStore.viewerSettings.toJS) {
            this.topicViewerStore.setSettings({ [key]: state[key] });
          }
        }
      });
    }

    // Trigger Recognition re-render after applying snapshot
    this.forceRecognitionUpdate();
  }

  updateQuery() {
    const nextParams: Record<string, string> = {};

    if (queryAPI.getKey(ToolbarQuery.Debug) === '1') {
      nextParams[ToolbarQuery.Debug] = '1';
    }

    if (this.isJoinedConference) {
      nextParams[ToolbarQuery.ConferenceState] = this.conferenceState;
    }

    queryAPI.replace(nextParams);
  }

  toggleLeftSidebar(state: boolean) {
    this.isLeftSidebarOpen = state;
  }

  toggleRightSidebar(state: boolean) {
    this.isRightSidebarOpen = state;
  }

  triggerRecognitionRerender() {
    // This will trigger a re-render of components that depend on this property
    // Force a MobX reaction by updating an observable property
    this.isFetching = this.isFetching;
  }

  // Add a property that changes to trigger re-renders
  recognitionUpdateTrigger = 0;

  forceRecognitionUpdate() {
    this.recognitionUpdateTrigger += 1;
  }

  dispose() {
    this.isFetching = true;

    this.resetConferenceState();

    this.topicViewerStore.dispose();

    this.setActiveTab(this.topic?.areRemarksAvailable ? SidebarTab.Remarks : SidebarTab.Participants);

    this.error = null;
    this.topic = null;

    this.reactions.map((dispose) => dispose());
  }

  private resetConferenceState() {
    this.topic?.setOnlineUsers([]);

    this.topicWSStore.close();

    this.setListenMode(false);

    this.conferenceState = ConferenceState.None;

    this.topicViewerStore.viewer?.setPointers([]);
  }

  private subscribeToReactions() {
    this.reactions = [
      reaction(
        () => [this.conferenceState, this.me?.isMaster],
        ([conferenceState, isMaster], [, prevIsMaster]) => {
          this.setListenMode(conferenceState === ConferenceState.Follow && !isMaster);

          if (conferenceState === ConferenceState.Follow && isMaster && isMaster !== prevIsMaster) {
            this.notifyUserIsMaster();
          }
        },
      ),
      reaction(
        () => this.conferenceState,
        () => this.updateQuery(),
      ),
      reaction(
        () => this.settings,
        () => this.saveSettings(),
      ),
    ];
  }

  private applyQuery() {
    if (queryAPI.has(ToolbarQuery.ConferenceState)) {
      const value = queryAPI.getKey(ToolbarQuery.ConferenceState);

      if (isEnumMember(value, ConferenceState)) {
        this.conferenceState = queryAPI.getKey(ToolbarQuery.ConferenceState);

        if (this.isJoinedConference) {
          this.joinConference();
        }
      }
    }
  }

  private saveSettings = debounce(() => {
    if (!this.topic) {
      return;
    }

    let shouldSave = true;
    const defaultSnapshot = this.topic.defaultSnapshot;

    if (defaultSnapshot?.state) {
      // update default snapshot only if values are different
      if (JSON.stringify(defaultSnapshot?.state) === JSON.stringify(this.settings)) {
        shouldSave = false;
      }
    }

    if (shouldSave) {
      this.topicAPI.updateTopicSnapshot(this.topic.id, DEFAULT_SNAPSHOT_ID, {
        data: { state: this.settings },
        type: 'private',
      });
    }
  }, 1000);

  private notifyUserIsMaster() {
    NotifyService.success('You are conference master!');
  }
}

let topicStore: TopicStore | undefined;
let TopicStoreContext: Context<TopicStore> | undefined;

export function useTopicStore(): TopicStore {
  if (!TopicStoreContext) {
    throw new Error(`Context "TopicStoreContext" was not created!`);
  }

  return useContext(TopicStoreContext);
}

export function useTopicWSStore(): TopicWSStore {
  return useTopicStore().topicWSStore;
}

export function useTopicViewerStore(): TopicViewerStore {
  return useTopicStore().topicViewerStore;
}

export function withTopicStore(Component: FunctionComponent<Any>) {
  return function withTopicStoreComponent(props: Record<string, unknown>) {
    topicStore = new TopicStore();
    TopicStoreContext = createContext(topicStore);

    return (
      <TopicStoreContext.Provider value={topicStore}>
        <Component {...props} />
      </TopicStoreContext.Provider>
    );
  };
}
